import React, {useEffect, useState} from 'react';
import {
    ComboBox,
    TextField
} from "@fluentui/react";
import {cloneDeep} from "lodash";
import {useGetMetadataQuery} from "../../store/Api";
import {IProductBase} from "../../models/Search";
import CurrencyDisplay from '../Currency/CurrencyDisplay';

interface IBasicDetailsTableProps {
    product: IProductBase;
    onChange?: (product: IProductBase) => void;
    editMode?: boolean;
    catalog: string;
}

const BasicDetailsTable: React.FC<IBasicDetailsTableProps> = (props) => {
    const metadata = useGetMetadataQuery(props.catalog);
    const [segments, setSegments] = useState<{ key: string, text: string }[]>([]);
    const [categories, setCategories] = useState<{ key: string, text: string }[]>([]);

    const [newSegmentEnabled, setNewSegmentEnabled] = useState(false);
    const [newCatEnabled, setNewCatEnabled] = useState(false);

    const [msrp, setMsrp] = useState<string | undefined>(props.product.msrp?.toString());

    useEffect(() => {
        if (metadata.data?.segments === undefined) return;
        setSegments([
            ...Object.keys(metadata.data?.segments).map(d => ({
                key: d,
                text: d
            })),
            {key: 'NEW', text: 'New Segment'}
        ])
    }, [metadata.data?.segments])
    useEffect(() => {
        if (segments === undefined || segments.length === 0 || !props.product.segment || props.product.segment === '') {
            setCategories([])
            return;
        }
        
        const cats = metadata.data?.segments[props.product.segment]
        
        setCategories([...(cats?.map((v) => {return {key: v, text: v}}) ?? []), {key: 'NEW', text: 'New Category'}])
    }, [segments, metadata.data?.segments, props.product.segment])

    useEffect(() => {
        setMsrp(props.product.msrp?.toString());
    }, [props.product.msrp]);
    
    const checkDisabled =() => {
        return (!props.product.name || props.product.name === '') || (!props.product.manufacturerPartNumber || props.product.manufacturerPartNumber === '')
    }

    const basicDetails = [
        {
            key: 'Name',
            value: props.product.name,
            edit: <TextField
                tabIndex={0}
                required={true}
                errorMessage={!props.product.name || props.product.name === '' ? 'Name is required' : undefined}
                value={props.product.name}
                disabled={!!props.product.descriptionSource}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.name = d ?? '';
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Manufacturer Part Number',
            value: props.product.manufacturerPartNumber,
            edit: <TextField
                tabIndex={0}
                required={true}
                errorMessage={!props.product.manufacturerPartNumber || props.product.manufacturerPartNumber === '' ? 'Manufacturer Part Number is required' : undefined}
                value={props.product.manufacturerPartNumber}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.manufacturerPartNumber = d ?? '';
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Manufacturer',
            value: props.product.manufacturer,
            edit: <TextField
                value={props.product.manufacturer}
                disabled={!!props.product.descriptionSource || checkDisabled()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.manufacturer = d ?? '';
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Internal Part Number',
            value: props.product.internalPartNumber,
            edit: <TextField
                disabled={checkDisabled()}
                value={props.product.internalPartNumber}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.internalPartNumber = d ?? '';
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'MSRP',
            value: <CurrencyDisplay value={props.product.msrp} />,
            edit: <TextField
                value={msrp}
                disabled={checkDisabled()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    setMsrp(d);
                }}
                onBlur={() => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.msrp = parseFloat(msrp ?? '0');
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Segment',
            value: props.product.segment,
            edit: <>
                <ComboBox
                    autoComplete={"on"}
                    options={segments}
                    disabled={metadata.isLoading || !!props.product.descriptionSource || checkDisabled()}
                    selectedKey={newSegmentEnabled ? 'NEW' : props.product.segment}
                    onChange={(e, d) => {
                        if (!props.onChange) return;
                        if (d?.key === 'NEW') setNewSegmentEnabled(true)
                        else {
                            setNewSegmentEnabled(false);
                            const newProduct = cloneDeep(props.product);
                            newProduct.segment = d?.key.toString() ?? '';
                            props.onChange(newProduct);
                        }
                    }}
                />
                {newSegmentEnabled && <TextField
                    value={props.product.segment}
                    disabled={checkDisabled()}
                    onChange={(e,d) => {
                        if (!props.onChange) return;
                        const newProduct = cloneDeep(props.product);
                        newProduct.segment = d ?? '';
                        props.onChange(newProduct);
                    }}
                    />}
            </>,
        },
        {
            key: 'Category',
            value: props.product.category,
            edit: <>
                <ComboBox
                    autoComplete={"on"}
                    options={categories}
                    disabled={metadata.isLoading || !!props.product.descriptionSource || checkDisabled()}
                    selectedKey={newCatEnabled ? 'NEW' : props.product.category}
                    onChange={(e, d) => {
                        if (!props.onChange) return;
                        if (d?.key === 'NEW') setNewCatEnabled(true)
                        else {
                            setNewCatEnabled(false);
                            const newProduct = cloneDeep(props.product);
                            newProduct.category = d?.key.toString() ?? '';
                            props.onChange(newProduct);
                        }
                    }}
                />
                {newCatEnabled && <TextField
                    value={props.product.category}
                    disabled={checkDisabled()}
                    onChange={(e,d) => {
                        if (!props.onChange) return;
                        const newProduct = cloneDeep(props.product);
                        newProduct.category = d ?? '';
                        props.onChange(newProduct);
                    }}
                />}
            </>,
        },
        {
            key: 'Weight (lb)',
            value: props.product.weight,
            edit: <TextField
                disabled={checkDisabled()}
                suffix="lb"
                value={props.product.weight?.toString()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.weight = parseFloat(d ?? '0');
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Width (in)',
            value: props.product.width,
            edit: <TextField
                disabled={checkDisabled()}
                suffix="in"
                value={props.product.width?.toString()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.width = parseFloat(d ?? '0');
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Height (in)',
            value: props.product.height,
            edit: <TextField
                disabled={checkDisabled()}
                suffix="in"
                value={props.product.height?.toString()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.height = parseFloat(d ?? '0');
                    props.onChange(newProduct);
                }}
            />,
        },
        {
            key: 'Length (in)',
            value: props.product.length,
            edit: <TextField
                disabled={checkDisabled()}
                suffix="in"
                value={props.product.length?.toString()}
                onChange={(e, d) => {
                    if (!props.onChange) return;
                    const newProduct = cloneDeep(props.product);
                    newProduct.length = parseFloat(d ?? '0');
                    props.onChange(newProduct);
                }}
            />,
        }
    ];

    return (
        <>
            <h3>{"Basic Details"}</h3>
            <div className={"ac-attribute-segment"}>
                {basicDetails.map((item) => (
                    <div key={item.key} style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontWeight: 600 }}>{item.key}</label>
                        {props.editMode ? item.edit : item.value && item.value != '' ? item.value : '(Not Set)'}
                    </div>
                ))}
           
            </div>
        </>
    )
}

export default BasicDetailsTable;